import { foldHost } from './citationSources.utils';
import { PASSAGE_TONE_LABEL } from '../toneMetric';

/**
 * Pure, React-free helpers for the AI answer detail screen. Kept separate from the
 * component so the answer-painting logic can be unit tested without pulling in ReactMarkdown.
 */

/** The badge shown beside a recommendation the analysis did not agree with itself about. */
export const AMBIGUOUS_RECOMMENDATION_LABEL = 'Ambiguous';

/** Why that badge is there, in the reader's terms rather than the pipeline's. */
export const AMBIGUOUS_RECOMMENDATION_TOOLTIP = 'The analysis passes disagreed about which brand this answer recommends.';

/**
 * Whether this run's recommendation should be shown as ambiguous.
 *
 * The only evidence is the parser's own `recVoteDisagreement === true` — the majority-of-3
 * recommendation vote did not agree with itself. Ambiguity is never inferred from anything else:
 * not a missing winner, not a tie in share of voice, not two close scores. Those situations have
 * their own displays, and hedging them here would mark answers the analysis was certain about.
 *
 * The parsed document arrives loosely typed across the REST boundary, so it is narrowed here and
 * only a real boolean `true` counts.
 */
export function isAmbiguousRecommendation(parsed?: unknown): boolean {
  if (!parsed || typeof parsed !== 'object') {
    return false;
  }

  return (parsed as { recVoteDisagreement?: unknown }).recVoteDisagreement === true;
}

/** A stable machine key from a runtime/serialized revision reference (or a bare string). */
export function referenceKey(reference: unknown): string {
  if (typeof reference === 'string') {
    return reference.trim();
  }
  if (!reference || typeof reference !== 'object') {
    return '';
  }
  const record = reference as { getStableKey?: () => string; key?: unknown };
  return String(record.getStableKey?.() || record.key || '').trim();
}

/** Structural view of a parsed citation — only the fields the "Sources cited" box needs. */
export interface CitationLike {
  url: string;
  domain?: string;
  title?: string;
  entityName?: string;
  mentionCount?: number;
  firstSeenOrder?: number;
}

/** One source, ready to render: the link text is resolved, never left to the caller. */
export interface CitationEntry<T extends CitationLike = CitationLike> {
  citation: T;
  /** What the link reads as. The title when the data has one; the URL only as a last resort. */
  label: string;
}

/**
 * What a cited page's link should read as.
 *
 * The stored title, because the hostname is the row it sits under and repeating it would tell the
 * reader nothing about *which* page was cited. Only a citation with no title at all falls back to
 * its URL.
 */
export function citationLabel(citation: CitationLike): string {
  return citation.title?.trim() || citation.url;
}

/**
 * Every hostname a citation could be filed under: the one the analysis recorded and the one its
 * URL actually carries.
 *
 * Both are kept because they can disagree — a citation may record a registrable domain while its
 * URL names a subdomain — and a page must appear under the source it belongs to whichever of the
 * two the mirror grouped on.
 */
export function citationHostsOf(citation: CitationLike): string[] {
  const hosts = new Set<string>();

  const recorded = foldHost(citation.domain);
  if (recorded) {
    hosts.add(recorded);
  }

  try {
    const fromUrl = foldHost(new URL(citation.url).hostname);
    if (fromUrl) {
      hosts.add(fromUrl);
    }
  } catch {
    // A citation whose URL will not parse keeps whatever the analysis recorded; it simply files
    // under nothing rather than under a made-up host.
  }

  return Array.from(hosts);
}

/**
 * Whether one parsed citation belongs under one grouped source.
 *
 * Matches the group's own member hostnames first — those came from the very mirror rows the group
 * was built from — and then accepts a subdomain of the group key. That suffix rule only decides
 * which pages appear under a row the server already grouped, so it cannot merge two publishers the
 * way a browser-side registrable-domain guess could.
 */
export function citationMatchesSource(citation: CitationLike, group: { key: string; hosts: readonly string[] }): boolean {
  const key = foldHost(group.key);
  return citationHostsOf(citation).some((host) => group.hosts.includes(host) || host === key || (!!key && host.endsWith(`.${key}`)));
}

/**
 * The individual pages one grouped source was cited through, strongest first.
 *
 * Ordered by how often the answer leaned on each page, then by the order they first appeared, so
 * two equally-cited pages keep the sequence the answer itself gave them.
 */
export function selectSourceCitations<T extends CitationLike>(
  citations: readonly T[] | undefined,
  group: { key: string; hosts: readonly string[] }
): CitationEntry<T>[] {
  return (citations || [])
    .filter((citation) => citationMatchesSource(citation, group))
    .slice()
    .sort((left, right) => (right.mentionCount || 0) - (left.mentionCount || 0) || (left.firstSeenOrder || 0) - (right.firstSeenOrder || 0))
    .map((citation) => ({ citation, label: citationLabel(citation) }));
}

/** One entity chip on a cited-source row. */
export interface SourceEntityChip {
  termKey: string;
  citations: number;
  isOwn: boolean;
}

/**
 * Orders the entities a source was cited for.
 *
 * Own-side entities lead, whatever their share — the same "You must always be shown" rule the
 * mentions column follows. A reader scanning which sources back their brand should never have to
 * find "You" behind a competitor that happened to be cited once more.
 */
export function orderSourceEntities(
  entities: readonly { termKey: string; citations: number }[] | undefined,
  isOwn: (termKey: string) => boolean
): SourceEntityChip[] {
  return (entities || [])
    .map((entity) => ({ termKey: entity.termKey, citations: entity.citations, isOwn: isOwn(entity.termKey) }))
    .sort(
      (left, right) =>
        Number(right.isOwn) - Number(left.isOwn) || right.citations - left.citations || left.termKey.localeCompare(right.termKey)
    );
}

/** A run of consecutive markdown lines sharing a single brand tint (or none). */
export interface PaintedSegment {
  key: string;
  text: string;
  color?: string;
  /** Taxonomy term key of the entity this run is about — for the hover tooltip. */
  entityKey?: string;
  /** That entity's share of the passage (0-1), when the unit went through credit splitting. */
  share?: number;
  /** True when the unit covered several subjects and this one merely dominated. */
  mixed?: boolean;
  /** Absolute markdown line range this run covers — used by the hover spotlight. */
  lineStart: number;
  lineEnd: number;
}

/** Structural view of the parsed unit — only the fields painting needs. */
export type PaintUnit = { id: string; debrisClass?: unknown; lineStart?: number; lineEnd?: number };

/** Structural view of a parsed resolution record — only the fields painting needs. */
export type PaintResolution = { unitId: string; entity: unknown; weight?: number };

/**
 * One credited cell of a column-keyed comparison table: the 0-based column index in grid order
 * (identical to the rendered `<td>` order of that row) and the entity credited there.
 */
export type PaintCellSubject = { column: number; subject: unknown };

/** Structural view of a parsed label record — only the fields painting needs. */
export type PaintLabel = {
  unitId: string;
  subjects?: readonly unknown[];
  /**
   * Present only on table-row labels of column-keyed tables, and only once the extraction that
   * produces it has run. Sparse: uncredited, empty and conflicted columns are absent — including
   * the key column, which names the attribute rather than a brand.
   */
  cellSubjects?: readonly PaintCellSubject[];
  /**
   * Compared alternatives the catalogue does not track. A row comparing one tracked brand against
   * untracked ones has a SINGLE subject but is still a comparison — counting only `subjects` made
   * such a row read as being about that one brand.
   */
  untrackedCoSubjects?: readonly string[];
};

/**
 * How close the top two subjects of a multi-subject unit may sit before the unit counts as
 * neutral — covering both brands rather than being about either.
 */
export const NEUTRAL_DOMINANCE_MARGIN = 0.15;

/** The entity a unit is about, with the share it was credited and whether it merely dominated. */
interface DominantSubject {
  entityKey: string;
  share?: number;
  mixed: boolean;
}

/**
 * Picks the entity a unit is *about*, given its labelled subjects.
 *
 * `labels[].subjects` is the base layer: it is present for every unit the analysis understood,
 * the single-subject majority included. `resolution[]` only carries rows for units that went
 * through Pass-2 credit splitting — units with several subjects to divide credit between — so it
 * is consulted solely to break a multi-subject tie by weight. A multi-subject unit with no
 * resolution row falls back to its first subject rather than going unpainted.
 */
function dominantSubject(
  unitId: string,
  subjectKeys: readonly string[],
  resolution: readonly PaintResolution[] | undefined
): DominantSubject | undefined {
  if (!subjectKeys.length) {
    return undefined;
  }

  if (subjectKeys.length === 1) {
    return { entityKey: subjectKeys[0], mixed: false };
  }

  const weighted = (resolution || [])
    .filter((record) => record.unitId === unitId && typeof record.weight === 'number')
    .sort((a, b) => (b.weight as number) - (a.weight as number));

  if (!weighted.length) {
    return { entityKey: subjectKeys[0], mixed: true };
  }

  // Neutral dominance: when the top two subjects are within a hair of each other the unit is not
  // "about" either of them — it covers both. Tinting it for the winner reads as a claim the text
  // does not make, which is what painted the intro sentence of a comparison answer as one brand.
  if (weighted.length > 1 && (weighted[0].weight as number) - (weighted[1].weight as number) <= NEUTRAL_DOMINANCE_MARGIN) {
    return undefined;
  }

  return {
    entityKey: referenceKey(weighted[0].entity) || subjectKeys[0],
    share: weighted[0].weight as number,
    // A dominant subject in a multi-subject unit still shares the passage: the spec asks for the
    // dominant tint plus an indicator that the passage is not exclusively about it.
    mixed: true,
  };
}

/** A table sub-unit id looks like `<unitId>.r<rowIndex>` (0-based over the table's body rows). */
function subUnitRowIndex(unitId: string, subUnitId: string): number | undefined {
  if (!subUnitId.startsWith(`${unitId}.r`)) {
    return undefined;
  }
  const index = Number.parseInt(subUnitId.slice(`${unitId}.r`.length), 10);
  return Number.isInteger(index) && index >= 0 ? index : undefined;
}

/**
 * The markdown line a table's body row occupies.
 *
 * A GFM table starts with its header row, then the `---` separator, then one line per body row —
 * so body row `N` (0-based) sits at `lineStart + 2 + N`. Pinned by a fixture test built from a
 * real markdown table rather than trusted by inspection.
 */
export const TABLE_BODY_ROW_OFFSET = 2;

/**
 * Splits the answer markdown into consecutive line-runs, each tinted by the brand the unit
 * covering those lines is about.
 *
 * Aboutness comes from `labels[].subjects`. Painting from `resolution[]` alone leaves most of
 * an on-brand answer uncoloured, because resolution rows exist only for the multi-subject units
 * that needed credit splitting — single-subject units carry their aboutness in `labels[]` and
 * have no resolution row at all. Units with no labelled subject, and debris units, stay
 * unpainted. Runs materialized before line spans / labels shipped fall back to a single untinted
 * segment, leaving the hard name highlights as the only colour.
 */
/** What one markdown source line is tinted with, if anything. */
export interface LinePaint {
  color: string;
  entityKey: string;
  share?: number;
  mixed: boolean;
  /**
   * The finished background, when the tint is not the brand colour's own light wash — the verdict
   * lens shades each grade to a strength of its own. Absent for the brand tints.
   */
  backgroundColor?: string;
  /** In the verdict lens: the passage grades another of its brands differently from the one it is shaded for. */
  gradedDifferently?: boolean;
  /**
   * The passage is marked by its tint and a bar in the full colour along its leading edge, as the
   * verdict lens marks every graded passage. Without it a passage carries its brand's tint alone,
   * and a table row only a slim stripe on its first cell.
   */
  accent?: boolean;
}

/**
 * The tint for every markdown source line, indexed by line number.
 *
 * This is the painter's real output. The renderer applies these to the RENDERED DOM — per element,
 * per table row — and never to the markdown source: splitting the source into per-unit fragments
 * and parsing each separately is what destroyed table syntax, because a lone `| a | b |` row is not
 * a table.
 */
export function buildLinePaint(
  markdown: string,
  units: readonly PaintUnit[] | undefined,
  labels: readonly PaintLabel[] | undefined,
  resolution: readonly PaintResolution[] | undefined,
  colorByTermKey: Map<string, string>
): (LinePaint | undefined)[] {
  const lines = markdown ? markdown.split('\n') : [];
  const blank: (LinePaint | undefined)[] = new Array(lines.length).fill(undefined);

  if (!markdown) {
    return [];
  }

  const paintable = (units || []).filter(
    (unit) => typeof unit.lineStart === 'number' && typeof unit.lineEnd === 'number' && !unit.debrisClass
  );

  if (!paintable.length || !(labels || []).length || colorByTermKey.size === 0) {
    return blank;
  }

  const paintableIds = new Set(paintable.map((unit) => unit.id));

  const labelsByUnitId = new Map<string, PaintLabel>();
  for (const label of labels || []) {
    if (!labelsByUnitId.has(label.unitId)) {
      labelsByUnitId.set(label.unitId, label);
    }
  }

  const subjectKeysByUnitId = new Map<string, string[]>();
  for (const label of labels || []) {
    const keys = (label.subjects || []).map((subject) => referenceKey(subject)).filter((key) => !!key);
    if (!keys.length) {
      continue;
    }
    // A unit can carry more than one label row; union their subjects rather than letting the
    // last one seen win, so a multi-subject unit is still recognised as multi-subject.
    subjectKeysByUnitId.set(label.unitId, [...(subjectKeysByUnitId.get(label.unitId) || []), ...keys]);
  }
  /**
   * A table row is MIXED when it covers more than one compared thing — tracked subjects and
   * untracked alternatives alike. A mixed row carries no single-brand tint: tinting it for its one
   * tracked subject would claim the row is about that brand, when the row is comparing it against
   * others the catalogue simply does not track. Rows with per-cell credit are handled by the cell
   * painter instead.
   */
  const isMixedRow = (unitId: string): boolean => {
    const label = labelsByUnitId.get(unitId);
    if (label?.cellSubjects?.length) return true;
    const subjects = (subjectKeysByUnitId.get(unitId) || []).length;
    const untracked = (label?.untrackedCoSubjects || []).length;
    return subjects + untracked > 1;
  };

  const paintByLine: (LinePaint | undefined)[] = new Array(lines.length).fill(undefined);

  // Wider units first (table before its rows); first assignment wins so a nested sub-unit
  // never repaints the lines its parent already claimed.
  const ordered = paintable
    .slice()
    .sort((a, b) => (a.lineStart as number) - (b.lineStart as number) || (b.lineEnd as number) - (a.lineEnd as number));

  const paintLine = (lineIndex: number, paint: LinePaint) => {
    if (lineIndex >= 0 && lineIndex < lines.length && paintByLine[lineIndex] === undefined) {
      paintByLine[lineIndex] = paint;
    }
  };

  const paintFor = (unitId: string): LinePaint | undefined => {
    const dominant = dominantSubject(unitId, subjectKeysByUnitId.get(unitId) || [], resolution);
    if (!dominant) {
      return undefined;
    }
    const color = colorByTermKey.get(dominant.entityKey);
    return color ? { color, entityKey: dominant.entityKey, share: dominant.share, mixed: dominant.mixed } : undefined;
  };

  for (const unit of ordered) {
    // A table row can arrive two ways. Preferred: the backend persists each row as its own UNIT
    // with real line positions, in which case it is painted like any other unit by the loop and
    // needs nothing special here. Fallback: the table is one unit and only the labels reference
    // `<unitId>.rN` sub-units, with no positions of their own — then the row line is derived from
    // the table's lineStart. The persisted form always wins because its positions are measured
    // rather than computed.
    const subUnitRows = Array.from(subjectKeysByUnitId.keys())
      .map((subUnitId) => ({ subUnitId, rowIndex: subUnitRowIndex(unit.id, subUnitId) }))
      .filter((entry): entry is { subUnitId: string; rowIndex: number } => entry.rowIndex !== undefined)
      // A sub-unit that IS persisted as its own paintable unit is handled by the main loop.
      .filter((entry) => !paintableIds.has(entry.subUnitId));

    if (subUnitRows.length) {
      for (const { subUnitId, rowIndex } of subUnitRows) {
        // A comparison table puts ONE BRAND PER COLUMN, so a row label naming several subjects
        // describes the row, not any single brand within it. Tinting the whole row for the
        // dominance-ordered first subject is wrong by construction, so such a row stays neutral
        // until the backend ships per-cell column-to-entity mappings.
        if (isMixedRow(subUnitId)) {
          continue;
        }
        const rowPaint = paintFor(subUnitId);
        if (rowPaint) {
          paintLine((unit.lineStart as number) + TABLE_BODY_ROW_OFFSET + rowIndex, rowPaint);
        }
      }
      continue;
    }

    // The same rule for a table row persisted as its own unit: several subjects means the row
    // compares brands rather than being about one.
    const isTableRow = subUnitRowIndex(unit.id.split('.r')[0], unit.id) !== undefined;
    if (isTableRow && isMixedRow(unit.id)) {
      continue;
    }

    const paint = paintFor(unit.id);
    if (!paint) {
      continue;
    }
    const start = Math.max(0, unit.lineStart as number);
    const end = Math.min(lines.length - 1, unit.lineEnd as number);
    for (let index = start; index <= end; index += 1) {
      paintLine(index, paint);
    }
  }

  return paintByLine;
}

/**
 * The painted line-runs of the answer, merged from {@link buildLinePaint}.
 *
 * Retained for the spotlight (which works in line ranges) and for the painter's own tests. The
 * renderer does NOT use these to slice the markdown — see the note on `buildLinePaint`.
 */
export function buildPaintedSegments(
  markdown: string,
  units: readonly PaintUnit[] | undefined,
  labels: readonly PaintLabel[] | undefined,
  resolution: readonly PaintResolution[] | undefined,
  colorByTermKey: Map<string, string>
): PaintedSegment[] {
  if (!markdown) {
    return [];
  }

  const lines = markdown.split('\n');
  const paintByLine = buildLinePaint(markdown, units, labels, resolution, colorByTermKey);

  // The document is unpaintable at all — no positioned units, no labels, or no colours to join to.
  // That is distinct from "nothing happened to paint", which still yields per-line segments.
  const paintable = (units || []).filter(
    (unit) => typeof unit.lineStart === 'number' && typeof unit.lineEnd === 'number' && !unit.debrisClass
  );
  if (!paintable.length || !(labels || []).length || colorByTermKey.size === 0) {
    return [{ key: 'all', text: markdown, lineStart: 0, lineEnd: Math.max(0, lines.length - 1), color: undefined }];
  }

  const sameRun = (a?: LinePaint, b?: LinePaint) =>
    a === b || (!!a && !!b && a.color === b.color && a.entityKey === b.entityKey && a.mixed === b.mixed);

  const segments: PaintedSegment[] = [];
  let index = 0;
  while (index < lines.length) {
    const paint = paintByLine[index];
    let end = index;
    while (end + 1 < lines.length && sameRun(paintByLine[end + 1], paint)) {
      end += 1;
    }
    segments.push({
      key: `${index}-${end}`,
      text: lines.slice(index, end + 1).join('\n'),
      lineStart: index,
      lineEnd: end,
      color: paint?.color,
      ...(paint ? { entityKey: paint.entityKey, share: paint.share, mixed: paint.mixed } : {}),
    });
    index = end + 1;
  }

  return segments;
}

/** A tint for one table cell. */
export interface CellPaint {
  color: string;
  entityKey: string;
  /** The finished background, as on {@link LinePaint}. Absent for the brand tints. */
  backgroundColor?: string;
  /** As on {@link LinePaint}: the cell carries a bar in the full colour along its leading edge. */
  accent?: boolean;
}

/** Line index -> column index -> tint. Only ever populated for table rows. */
export type CellPaintByLine = Map<number, Map<number, CellPaint>>;

/**
 * The markdown line a table row's label refers to, and the table it belongs to.
 *
 * A row arrives either as its own persisted unit (measured position — always preferred) or as a
 * `<tableUnitId>.rN` label with no unit of its own, in which case the line is derived from the
 * table's own position.
 */
export function resolveTableRow(units: readonly PaintUnit[] | undefined, unitId: string): { line: number; table: PaintUnit } | undefined {
  const all = units || [];
  const persisted = all.find((unit) => unit.id === unitId);

  for (const table of all) {
    const rowIndex = subUnitRowIndex(table.id, unitId);
    if (rowIndex === undefined || typeof table.lineStart !== 'number') {
      continue;
    }
    const line = typeof persisted?.lineStart === 'number' ? persisted.lineStart : table.lineStart + TABLE_BODY_ROW_OFFSET + rowIndex;
    return { line, table };
  }

  return undefined;
}

/**
 * The per-cell tints of every column-keyed comparison table in the document.
 *
 * Gated purely on the presence of `label.cellSubjects`: a document whose extraction predates the
 * field yields an empty map and the caller keeps its row-level rendering, in which a multi-subject
 * row stays neutral. Nothing is ever inferred from the cell text.
 *
 * The header row of a celled table is tinted with the same column mapping, so each column visibly
 * names the brand it belongs to.
 */
export function buildCellPaint(
  units: readonly PaintUnit[] | undefined,
  labels: readonly PaintLabel[] | undefined,
  colorByTermKey: Map<string, string>
): CellPaintByLine {
  const byLine: CellPaintByLine = new Map();
  if (!colorByTermKey.size) {
    return byLine;
  }

  /** Column mapping per table, reused to tint that table's header cells. */
  const columnsByTable = new Map<string, Map<number, CellPaint>>();

  const put = (line: number, column: number, paint: CellPaint) => {
    const row = byLine.get(line) ?? new Map<number, CellPaint>();
    if (!row.has(column)) {
      row.set(column, paint);
    }
    byLine.set(line, row);
  };

  for (const label of labels || []) {
    if (!label.cellSubjects?.length) {
      continue;
    }
    const resolved = resolveTableRow(units, label.unitId);
    if (!resolved) {
      continue;
    }

    const tableColumns = columnsByTable.get(resolved.table.id) ?? new Map<number, CellPaint>();

    for (const cell of label.cellSubjects) {
      if (typeof cell.column !== 'number' || cell.column < 0) {
        continue;
      }
      const entityKey = referenceKey(cell.subject);
      const color = entityKey ? colorByTermKey.get(entityKey) : undefined;
      if (!color) {
        continue;
      }
      const paint: CellPaint = { color, entityKey };
      put(resolved.line, cell.column, paint);
      if (!tableColumns.has(cell.column)) {
        tableColumns.set(cell.column, paint);
      }
    }

    columnsByTable.set(resolved.table.id, tableColumns);
  }

  // The header row sits on the table's first line.
  for (const [tableId, columns] of columnsByTable) {
    const table = (units || []).find((unit) => unit.id === tableId);
    if (!table || typeof table.lineStart !== 'number') {
      continue;
    }
    for (const [column, paint] of columns) {
      put(table.lineStart, column, paint);
    }
  }

  return byLine;
}

/** What a hover lights up: whole lines, plus individual cells of partially-matching table rows. */
export interface SpotlightTargets {
  lines: Set<number>;
  /** Line index -> the columns of that row which belong to the hovered subject. */
  cells: Map<number, Set<number>>;
}

/**
 * The lines and cells a hovered brand occupies.
 *
 * A table row that carries per-cell credit is spotlighted PER CELL — lighting the whole row would
 * claim the row is about one brand, which is exactly the reading the per-cell data exists to
 * correct. Every other unit is still selected whole.
 */
export function spotlightTargets(
  units: readonly PaintUnit[] | undefined,
  labels: readonly PaintLabel[] | undefined,
  resolution: readonly PaintResolution[] | undefined,
  termKeys: readonly string[] | undefined
): SpotlightTargets {
  const wanted = new Set((termKeys || []).map((key) => String(key || '').trim()).filter((key) => !!key));
  const cells = new Map<number, Set<number>>();
  const celledUnitIds = new Set<string>();

  for (const label of labels || []) {
    if (!label.cellSubjects?.length) {
      continue;
    }
    celledUnitIds.add(label.unitId);

    const resolved = resolveTableRow(units, label.unitId);
    if (!resolved) {
      continue;
    }
    for (const cell of label.cellSubjects) {
      if (typeof cell.column === 'number' && wanted.has(referenceKey(cell.subject))) {
        const columns = cells.get(resolved.line) ?? new Set<number>();
        columns.add(cell.column);
        cells.set(resolved.line, columns);
      }
    }
  }

  // Whole-unit selection, minus the rows whose cells answer for themselves.
  const unitIds = new Set(
    Array.from(unitIdsForEntityTermKeys(labels, resolution, termKeys)).filter((unitId) => !celledUnitIds.has(unitId))
  );

  return { lines: lineIndicesForUnits(units, unitIds), cells };
}

/** One entity's measurement of one attribute, as `run.aspectScores[]` records it. */
export interface AttributeScoreLike {
  entity?: unknown;
  aspectTerm?: unknown;
  /** Fraction (0-1) of the answer's non-debris text discussing this attribute for this entity. */
  share: number;
}

export interface AttributeEntry {
  entityKey: string;
  share: number;
}

export interface AttributeGroup {
  aspectTermKey: string;
  entries: AttributeEntry[];
}

/**
 * Groups `run.aspectScores[]` by attribute for the detail sidebar's comparison table.
 *
 * The displayed share is `aspectScores[].share` unchanged — this only reshapes the rows. Records
 * without an attribute term are dropped rather than grouped under a blank heading. The records' own
 * score is not carried: it is a separate measurement from the verdict, and the table reads each
 * cell's verdict from the passages it points at instead (`buildAttributeVerdicts`).
 */
export function buildAttributeGroups(aspectScores: readonly AttributeScoreLike[] | undefined): AttributeGroup[] {
  const groups = new Map<string, AttributeGroup>();

  for (const aspectScore of aspectScores || []) {
    const aspectTermKey = referenceKey(aspectScore.aspectTerm);
    if (!aspectTermKey) {
      continue;
    }
    const group = groups.get(aspectTermKey) ?? { aspectTermKey, entries: [] };
    group.entries.push({ entityKey: referenceKey(aspectScore.entity), share: aspectScore.share });
    groups.set(aspectTermKey, group);
  }

  const list = Array.from(groups.values());
  for (const group of list) {
    group.entries.sort((a, b) => b.share - a.share);
  }

  // Attributes are ordered by how much of the answer discusses them in total — the attribute's
  // share summed across every entity measured on the run. The table reads one attribute across all
  // entities per row, so the row that occupied most of the answer belongs at the top; ordering by
  // any single entity's share would rank the whole table by whichever brand came first.
  //
  // Ties break on the term key, which is stable and always present. The display name is resolved
  // asynchronously per row, so sorting on the name would let the table reorder as names arrive.
  return list.sort(
    (left, right) => combinedAttributeShare(right) - combinedAttributeShare(left) || left.aspectTermKey.localeCompare(right.aspectTermKey)
  );
}

/** How much of the answer discusses this attribute in total, across every entity measured. */
export function combinedAttributeShare(group: AttributeGroup): number {
  return group.entries.reduce((total, entry) => total + (Number.isFinite(entry.share) ? entry.share : 0), 0);
}

/** The share as a whole percentage of the answer's text. */
export function shareToPercent(share: number): number {
  if (typeof share !== 'number' || !Number.isFinite(share)) {
    return 0;
  }
  return Math.max(0, Math.min(100, Math.round(share * 100)));
}

/**
 * A plain-language sense of how much text a percentage is — "roughly a fifth", "about half".
 * Deliberately coarse: the point is to make a bare number mean something, not to restate it.
 */
export function describeShareFraction(percent: number): string {
  if (percent <= 0) return 'almost none of the text';
  if (percent < 8) return 'a small part of the text';
  if (percent < 17) return 'roughly a tenth of the text';
  if (percent < 23) return 'roughly a fifth of the text';
  if (percent < 29) return 'roughly a quarter of the text';
  if (percent < 42) return 'roughly a third of the text';
  if (percent < 58) return 'about half of the text';
  if (percent < 71) return 'roughly two thirds of the text';
  if (percent < 88) return 'most of the text';
  return 'nearly all of the text';
}

/** Tooltip for an attribute share cell: what the number measures, in the reader's terms. */
export function describeAttributeShare(percent: number, entityLabel?: string, attributeLabel?: string): string {
  const attribute = attributeLabel?.trim() ? `"${attributeLabel.trim()}"` : 'this attribute';
  const forEntity = entityLabel?.trim() ? ` for ${entityLabel.trim()}` : '';
  return `How much of this answer discusses ${attribute}${forEntity} — ${percent}% means ${describeShareFraction(percent)}.`;
}

/**
 * The narrowest bar drawn for a measured, non-zero share, as a percentage of the bar's track.
 *
 * A legibility floor for the fill only. An attribute discussed in one sentence of a long answer is
 * still a finding, and a sliver too thin to see would drop it from the table without a trace; the
 * exact share stays in the bar's accessible name and tooltip.
 */
const MINIMUM_VISIBLE_ATTRIBUTE_BAR_PERCENT = 4;

/**
 * The largest share any drawn cell holds — the full width every attribute bar is measured against.
 *
 * An attribute's share is a small fraction of an answer: a well-covered one is a fifth of the text,
 * one mentioned in passing a hundredth. Against the whole answer the passing mention draws as
 * nothing; against the largest share in the table each bar shows its weight relative to the others,
 * and the most-discussed attribute runs the full width.
 *
 * Only the cells the table draws count — the given rows, in the given entity columns. Undefined when
 * none of them measured anything, so no bar is drawn rather than one divided by zero.
 */
export function attributeShareCeiling(groups: readonly AttributeGroup[], entityKeys: readonly string[]): number | undefined {
  const columns = new Set(entityKeys);
  let ceiling = 0;
  for (const group of groups) {
    for (const entry of group.entries) {
      if (columns.has(entry.entityKey) && Number.isFinite(entry.share) && entry.share > ceiling) {
        ceiling = entry.share;
      }
    }
  }
  return ceiling > 0 ? ceiling : undefined;
}

/**
 * How wide one cell's bar is drawn, as a percentage of its track: its share against the table's
 * ceiling, floored so a measured share never vanishes. A share that measured nothing draws nothing.
 */
export function attributeBarWidthPercent(share: number, ceiling: number | undefined): number {
  if (ceiling === undefined || !(ceiling > 0) || typeof share !== 'number' || !Number.isFinite(share) || share <= 0) {
    return 0;
  }
  return Math.min(100, Math.max(MINIMUM_VISIBLE_ATTRIBUTE_BAR_PERCENT, (share / ceiling) * 100));
}

/**
 * The accessible name and tooltip of an attribute bar: the share it measures, and what its length is
 * drawn against. The cell prints no number, so this is where the figure lives. Without a ceiling —
 * nothing in the table measured anything — no bar is drawn, so there is nothing to compare against.
 */
export function describeAttributeBar(
  percent: number,
  ceilingPercent: number | undefined,
  entityLabel?: string,
  attributeLabel?: string
): string {
  const share = describeAttributeShare(percent, entityLabel, attributeLabel);
  return ceilingPercent === undefined
    ? share
    : `${share} The bar is drawn against the most-discussed attribute in this answer (${ceilingPercent}%).`;
}

/**
 * Tooltip distinguishing the thumb from the share it sits beside. The thumb combines its passages'
 * grades, so it carries the passages' own name for their reading, which does not follow the switch.
 */
export const ATTRIBUTE_VERDICT_HINT = `${PASSAGE_TONE_LABEL} when this attribute is discussed`;

/** Column sub-label naming what the attribute bars measure. */
export const ATTRIBUTE_SHARE_COLUMN_LABEL = 'Share of answer';

/** Structural view of a parsed aspect record — only what the attribute spotlight needs. */
export type SpotlightAspect = { unitId: string; aspectTerm?: unknown };

/**
 * The units a brand is credited in — the same association the colorization uses.
 *
 * A unit counts when the brand appears in its labelled `subjects` OR carries a resolution record
 * for it, so a unit shared by two brands is selected by either of them. Term keys are passed in
 * because one brand can own several taxonomy terms.
 */
export function unitIdsForEntityTermKeys(
  labels: readonly PaintLabel[] | undefined,
  resolution: readonly PaintResolution[] | undefined,
  termKeys: readonly string[] | undefined
): Set<string> {
  const wanted = new Set((termKeys || []).map((key) => String(key || '').trim()).filter((key) => !!key));
  const unitIds = new Set<string>();

  if (!wanted.size) {
    return unitIds;
  }

  for (const label of labels || []) {
    if ((label.subjects || []).some((subject) => wanted.has(referenceKey(subject)))) {
      unitIds.add(label.unitId);
    }
  }

  for (const record of resolution || []) {
    if (wanted.has(referenceKey(record.entity))) {
      unitIds.add(record.unitId);
    }
  }

  return unitIds;
}

/**
 * The units carrying an attribute, for ANY brand — an attribute spotlight answers "where was this
 * discussed at all", not "where was it discussed about one brand".
 */
export function unitIdsForAspectTerm(aspects: readonly SpotlightAspect[] | undefined, aspectTermKey: string): Set<string> {
  const wanted = String(aspectTermKey || '').trim();
  const unitIds = new Set<string>();

  if (!wanted) {
    return unitIds;
  }

  for (const aspect of aspects || []) {
    if (referenceKey(aspect.aspectTerm) === wanted && aspect.unitId) {
      unitIds.add(aspect.unitId);
    }
  }

  return unitIds;
}

/**
 * The markdown lines those units occupy, so the answer can spotlight them.
 *
 * A unit with no line span contributes nothing rather than guessing; a `<unitId>.rN` sub-unit that
 * was not persisted as its own unit falls back to its parent table's derived row line, matching
 * how the painter places it.
 */
export function lineIndicesForUnits(units: readonly PaintUnit[] | undefined, unitIds: ReadonlySet<string>): Set<number> {
  const lines = new Set<number>();
  if (!unitIds.size) {
    return lines;
  }

  const byId = new Map((units || []).map((unit) => [unit.id, unit] as const));

  const addSpan = (unit: PaintUnit | undefined) => {
    if (!unit || typeof unit.lineStart !== 'number' || typeof unit.lineEnd !== 'number') {
      return false;
    }
    for (let line = unit.lineStart; line <= unit.lineEnd; line += 1) {
      lines.add(line);
    }
    return true;
  };

  for (const unitId of unitIds) {
    if (addSpan(byId.get(unitId))) {
      continue;
    }
    // Not persisted as its own unit — derive the row line from its parent table.
    for (const parent of units || []) {
      const rowIndex = subUnitRowIndex(parent.id, unitId);
      if (rowIndex !== undefined && typeof parent.lineStart === 'number') {
        lines.add(parent.lineStart + TABLE_BODY_ROW_OFFSET + rowIndex);
        break;
      }
    }
  }

  return lines;
}

/**
 * What a sidebar row points at in the answer body.
 *
 * Named rather than resolved eagerly, because a subject now has two independent ways of being
 * active — pinned by a click, or merely hovered — and both have to survive the parsed document
 * arriving late. Holding the subject and resolving it at render means a selection made before the
 * parse lands lights up the moment it does, instead of having resolved to nothing forever.
 */
export type SpotlightSubject = { kind: 'entity'; name: string } | { kind: 'aspect'; termKey: string };

/**
 * The layout and affordance every row that pins a passage carries.
 *
 * The rows were interactive long before they looked it: hovering lit passages in the answer, but
 * nothing about a row said it could be pointed at, so the behaviour went unfound. The pointer
 * cursor and the hover tint are what advertise it, and they belong in one constant because the
 * mentions list and the attributes table have to read as the same kind of control.
 *
 * The negative margin against the padding lets the tint extend slightly past the text without
 * moving anything — the row occupies exactly the width it did before.
 */
export const SPOTLIGHT_ROW_CLASS_NAME = 'flex items-center gap-2 cursor-pointer rounded px-1 -mx-1 transition-colors';

/**
 * The same affordance for a row that is a real `<tr>`.
 *
 * A table row cannot carry the flex layout above without breaking the cell grid it exists to
 * define, and the rounding and negative margin have nothing to align to inside a table. What has to
 * match is the part the reader reads: the pointer cursor and the background states, which both
 * variants take from `spotlightRowStateClassName`.
 */
export const SPOTLIGHT_TABLE_ROW_CLASS_NAME = 'cursor-pointer transition-colors';

/**
 * The row's background for each of its three readings.
 *
 * Hover, pinned, and pinned-while-hovered have to be told apart at a glance: a reader running the
 * cursor over a pinned row must still see which row is pinned. Hover uses the page's existing
 * neutral tint; pinned uses the accent already carrying "selected" elsewhere on this screen, one
 * step darker while hovered.
 */
export function spotlightRowStateClassName(selected: boolean): string {
  return selected ? 'bg-sky-50 hover:bg-sky-100' : 'hover:bg-slate-50';
}

/** Whether two subjects name the same thing, so a second click can un-pin the first. */
export function isSameSpotlightSubject(left: SpotlightSubject | undefined, right: SpotlightSubject | undefined): boolean {
  if (!left || !right || left.kind !== right.kind) {
    return false;
  }
  if (left.kind === 'entity' && right.kind === 'entity') {
    return left.name === right.name;
  }
  if (left.kind === 'aspect' && right.kind === 'aspect') {
    return left.termKey === right.termKey;
  }
  return false;
}

/** Whether this subject is one of the pinned ones. */
export function isSpotlightSubjectSelected(selected: readonly SpotlightSubject[], subject: SpotlightSubject): boolean {
  return selected.some((candidate) => isSameSpotlightSubject(candidate, subject));
}

/**
 * Adds a subject to the pinned set, or removes it when it is already pinned.
 *
 * Membership, not replacement. Pinning a second brand must leave the first pinned: holding several
 * subjects at once is the point, and a single-slot selection would make every click undo the last —
 * a reader comparing two brands and an attribute needs all three lit together.
 */
export function toggleSpotlightSubject(selected: readonly SpotlightSubject[], subject: SpotlightSubject): SpotlightSubject[] {
  return isSpotlightSubjectSelected(selected, subject)
    ? selected.filter((candidate) => !isSameSpotlightSubject(candidate, subject))
    : [...selected, subject];
}

/**
 * The spotlight actually painted: everything EVERY pinned subject covers, plus the hovered one.
 *
 * A union over all of them rather than a replacement. Pinning is additive, and hover adds to the
 * pins rather than displacing them — letting either replace the other is what would make comparing
 * subjects impossible, since the passages a reader had just pinned would vanish the moment they
 * looked anywhere else.
 *
 * Every input being `undefined` means nothing is active at all, which leaves the answer at full
 * opacity. That stays distinct from a pinned subject resolving to an EMPTY set, which is a real
 * finding — "this subject is not attributable to any passage" — and contributes its honest
 * emptiness, so the answer dims.
 */
export function unionSpotlightTargets(targets: readonly (SpotlightTargets | undefined)[]): SpotlightTargets | undefined {
  const present = targets.filter((entry): entry is SpotlightTargets => !!entry);
  if (!present.length) {
    return undefined;
  }

  const lines = new Set<number>();
  const cells = new Map<number, Set<number>>();

  for (const target of present) {
    target.lines.forEach((line) => lines.add(line));
    for (const [line, columns] of target.cells) {
      const merged = cells.get(line) ?? new Set<number>();
      columns.forEach((column) => merged.add(column));
      cells.set(line, merged);
    }
  }

  return { lines, cells };
}
